import { memo } from 'react';
import { Minus, ShieldCheck, TrendingDown, TrendingUp } from 'lucide-react';
import { confidenceTone, formatLabel, stanceTone } from './semantics';
import type { CompanyNewsAnalysis } from './types';

interface ExecOverviewProps {
  data: CompanyNewsAnalysis;
  // Article-corpus breakdown — passed in so the KPI strip can show the full
  // hierarchy ('83 fetched · 77 analysed · 6 not relevant') instead of just
  // a flat total. When omitted, falls back to data.articles.length.
  analyzedCount?: number;
  skippedCount?: number;
}

function StanceIcon({ stance }: { stance: string }) {
  const n = stance.toLowerCase();
  const Icon = ['bullish', 'positive'].includes(n)
    ? TrendingUp
    : ['bearish', 'negative'].includes(n)
    ? TrendingDown
    : Minus;
  return <Icon className="h-4 w-4" />;
}

function ExecOverviewBase({ data, analyzedCount, skippedCount }: ExecOverviewProps) {
  const interp = data.investment_interpretation;
  const stance = interp?.stance;
  const confidence = data.confidence;
  const summary = interp?.summary;
  const bullish = interp?.bullish_factors ?? [];
  const bearish = interp?.bearish_factors ?? [];

  const stanceStyle = stanceTone(stance);
  const confStyle = confidenceTone(confidence);

  const totalArticles = data.articles.length;
  // When the caller supplies the breakdown, show all three numbers — otherwise
  // fall back to the legacy single-number display.
  const hasBreakdown = typeof analyzedCount === 'number' && typeof skippedCount === 'number';

  const hasSplit = bullish.length > 0 || bearish.length > 0;
  const bothCols = bullish.length > 0 && bearish.length > 0;

  return (
    <div className="overflow-hidden rounded-xl border border-neutral-200 bg-white">
      {/* ── stance + confidence + counts band ── */}
      <div className="flex flex-wrap items-center gap-x-6 gap-y-3 border-b border-neutral-100 px-6 py-4">
        {stance && (
          <div className="flex items-center gap-2">
            <p className="text-[10px] font-bold uppercase tracking-widest text-neutral-400">
              Outlook
            </p>
            <span
              className={`inline-flex items-center gap-1.5 rounded-md px-2.5 py-1 text-xs font-bold ${stanceStyle.pill}`}
            >
              <StanceIcon stance={stance} />
              {formatLabel(stance)}
            </span>
          </div>
        )}

        {confidence && (
          <div className="flex items-center gap-2">
            <p className="text-[10px] font-bold uppercase tracking-widest text-neutral-400">
              Confidence
            </p>
            <span
              className={`inline-flex items-center gap-1.5 rounded-md px-2.5 py-1 text-xs font-bold ${confStyle.pill}`}
            >
              <ShieldCheck className="h-3.5 w-3.5" />
              {formatLabel(confidence)}
            </span>
          </div>
        )}

        {/* Compact inline KPI strip — same look as the original, with the
            article breakdown discreetly tucked inside parentheses. Hovering
            the "articles" item surfaces the analysed / not-relevant split as
            a tooltip for users who want the full numbers. */}
        <div className="ml-auto flex items-center gap-5 text-[11px] font-medium text-neutral-500">
          <span
            title={
              hasBreakdown
                ? `${analyzedCount} analysed · ${skippedCount} not relevant`
                : undefined
            }
          >
            <span className="font-bold text-neutral-900">{totalArticles}</span>{' '}
            articles
            {hasBreakdown && (
              <span className="ml-1 text-neutral-400">
                ({analyzedCount} analysed
                {skippedCount! > 0 && `, ${skippedCount} skipped`})
              </span>
            )}
          </span>
          <span>
            <span className="font-bold text-neutral-900">{data.key_insights.length}</span>{' '}
            insights
          </span>
          {data.risks.length > 0 && (
            <span>
              <span className="font-bold text-neutral-900">{data.risks.length}</span>{' '}
              risks
            </span>
          )}
        </div>
      </div>

      {/* ── executive summary ── */}
      {summary && (
        <div className="px-6 py-5">
          <p className="text-[10px] font-bold uppercase tracking-widest text-neutral-400">
            Executive Summary
          </p>
          <p className="mt-2 text-sm leading-relaxed text-neutral-700">{summary}</p>
        </div>
      )}

      {/* ── Bullish / Bearish factor columns ── */}
      {hasSplit && (
        <div
          className={`grid border-t border-neutral-100 ${
            bothCols ? 'sm:grid-cols-2' : 'grid-cols-1'
          }`}
        >
          {bullish.length > 0 && (
            <div
              className={`px-6 py-5 ${
                bothCols ? 'sm:border-r sm:border-neutral-100' : ''
              }`}
            >
              <div className="mb-3 flex items-center gap-2">
                <TrendingUp className="h-3.5 w-3.5 text-emerald-600" />
                <p className="text-[10px] font-bold uppercase tracking-widest text-emerald-700">
                  Bullish Factors
                </p>
              </div>
              <ul className="space-y-2">
                {bullish.slice(0, 4).map((f, i) => (
                  <li
                    key={i}
                    className="flex items-start gap-2.5 text-xs leading-relaxed text-neutral-700"
                  >
                    <span
                      className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-emerald-500"
                      aria-hidden
                    />
                    <span>{f}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {bearish.length > 0 && (
            <div className="px-6 py-5">
              <div className="mb-3 flex items-center gap-2">
                <TrendingDown className="h-3.5 w-3.5 text-rose-600" />
                <p className="text-[10px] font-bold uppercase tracking-widest text-rose-700">
                  Bearish Factors
                </p>
              </div>
              <ul className="space-y-2">
                {bearish.slice(0, 4).map((f, i) => (
                  <li
                    key={i}
                    className="flex items-start gap-2.5 text-xs leading-relaxed text-neutral-700"
                  >
                    <span
                      className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-rose-500"
                      aria-hidden
                    />
                    <span>{f}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export const ExecOverview = memo(ExecOverviewBase);
